import React, { useState, useEffect, useCallback, startTransition } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import DesktopView from "../layout/DesktopView";
import SettingsCard from "./SettingsCard";
import SettingsEditor from "./SettingsEditor";
import { useToast } from "../common/ToastContext";
import { currentUserHasRole } from "../../services/userService";
import AccessDenied from "../common/AccessDenied";
import systemSettingsService from "../../services/systemSettingsService";
import { systemSettingKeys, defaultSettings } from "./defaultSettings";
import { v4 as uuidv4 } from "uuid";
import { useConfirmation } from "../common/ConfirmationContext";

const SettingsDesktop = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [goBack, setGoBack] = useState(null);
  const { showToast } = useToast();
  const [settings, setSettings] = useState([]);
  const [selectedSetting, setSelectedSetting] = useState(null);
  const [accessDenied, setAccessDenied] = useState(null);
  const { openConfirmation } = useConfirmation();

  const fetchSettings = useCallback(async () => {
    try {
      const fetchedSettings = await systemSettingsService.list();
      setSettings(fetchedSettings);
    } catch (error) {
      console.error("Error fetching settings:", error);
      showToast("Failed to fetch settings", "error");
    }
  }, [showToast]);

  useEffect(() => {
    const seed = async () => {
      await systemSettingsService.initialize(defaultSettings);
    };
    seed();
  }, []);

  useEffect(() => {
    const checkAccess = async () => {
      try {
        const hasAccess = await currentUserHasRole("admin");
        setAccessDenied(!hasAccess);
        if (hasAccess) {
          fetchSettings();
        }
      } catch (error) {
        console.error("Error checking user role:", error);
        setAccessDenied(true);
      }
    };
    checkAccess();
  }, [fetchSettings]);

  useEffect(() => {
    if (searchParams.has("id") && !selectedSetting) {
      const editSettingId = searchParams.get("id");
      const editSetting = settings.find(
        (setting) => setting.id === editSettingId,
      );
      if (editSetting) {
        const defaultSetting = defaultSettings.find(
          (ds) => ds.key === editSetting.key,
        );
        const settingWithDefaults = defaultSetting
          ? { ...editSetting, values: defaultSetting.values }
          : editSetting;
        setSelectedSetting(settingWithDefaults);
        if (searchParams.has("goback")) {
          setGoBack(searchParams.get("goback"));
          setSearchParams((prevParams) => {
            const newParams = new URLSearchParams(prevParams);
            newParams.delete("goback");
            return newParams;
          });
        }
      }
    }
  }, [searchParams, settings, selectedSetting, setSearchParams]);

  const handleSaveSetting = async (settingData) => {
    try {
      if (settingData.id) {
        await systemSettingsService.update({
          id: settingData.id,
          value: settingData,
        });
      } else {
        await systemSettingsService.create(settingData);
      }
      await fetchSettings();
      handleCloseSettingEditor();
      navigate(0);
      showToast("Setting saved successfully", "info");
    } catch (error) {
      console.error("Error saving setting:", error);
      showToast(`Error saving setting: ${error}`, "error");
    }
  };

  const handleDeleteSetting = async (settingId) => {
    try {
      openConfirmation({
        title: "Confirm Delete",
        message:
          "Are you sure you want to delete this setting? This action cannot be undone; but if this is a " +
          "default setting it will be automatically re-created with the system specified default value.",
        onConfirm: async () => {
          await systemSettingsService.delete(settingId);
          await fetchSettings();
          handleCloseSettingEditor();
          showToast("Setting deleted successfully", "info");
        },
      });
    } catch (error) {
      showToast(`Error deleting setting: ${error}`, "error");
    }
  };

  const handleCloseSettingEditor = () => {
    // Must commit with React Router v7's transitioned URL update, or the id effect reopens the editor.
    startTransition(() => setSelectedSetting(null));
    setSearchParams((prevParams) => {
      const newParams = new URLSearchParams(prevParams);
      newParams.delete("id");
      return newParams;
    });
    if (goBack) {
      const goto = goBack;
      setGoBack(null);
      navigate(goto + "?reload=true");
    }
  };

  const handleAddNewSetting = async () => {
    try {
      const createdSetting = await systemSettingsService.create({
        key: uuidv4(),
        value: {
          name: "New Setting",
          description: "Description for the new setting",
          content: "",
          format: "text",
        },
      });
      await fetchSettings();
      setSelectedSetting(createdSetting);
      setSearchParams((prevParams) => {
        const newParams = new URLSearchParams(prevParams);
        newParams.set("id", createdSetting.id);
        return newParams;
      });
      showToast("New setting created successfully", "info");
    } catch (error) {
      console.error("Error creating new setting:", error);
      showToast(`Error creating new setting: ${error}`, "error");
    }
  };

  const handleSettingClick = (setting) => {
    setSelectedSetting(setting);
    setSearchParams((prevParams) => {
      const newParams = new URLSearchParams(prevParams);
      newParams.set("id", setting.id);
      return newParams;
    });
  };

  const handleSearch = (term) => {
    // This function is left empty as search is handled internally by DesktopView
  };

  if (accessDenied === null) {
    return <div>Loading...</div>;
  }

  if (accessDenied) {
    return <AccessDenied />;
  }

  return (
    <DesktopView
      items={settings}
      selectedItem={selectedSetting}
      onSearch={handleSearch}
      searchPlaceholder="Filter settings ..."
      contextMenuItems={[]}
      onItemClick={handleSettingClick}
      onSave={handleSaveSetting}
      onDelete={handleDeleteSetting}
      onReset={() => {}} // Settings don't have a reset functionality
      onClose={handleCloseSettingEditor}
      Card={SettingsCard}
      Editor={SettingsEditor}
      addNewItem={handleAddNewSetting}
      eventName="settingCreated"
    />
  );
};

export default SettingsDesktop;
export { systemSettingKeys };
